import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { input } from 'zod';

import type { DialogType } from '../../../@types';
import type { WageringConfiguration } from '../../../@types/api';
import { QUERY_KEYS } from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { useToast } from '../../../contexts/ToastContext';
import { useInvalidateQuery } from '../../../hooks/useInvalidateQuery';
import { useToastErrorMessage } from '../../../hooks/useToastErrorMessage';
import { useWageringConfiguration } from '../../../queries/wageringConfigurations';
import { wageringConfigurationSchema, type WageringConfigurationFormData } from '../../../schema';
import { postData } from '../../../utils/api';
import FormModalLayout from '../../layouts/FormModalLayout';
import RetryAlert from '../../molecules/RetryAlert';
import { WelcomeBonusFormSection } from './welcome-bonus/WelcomeBonusFormPrimitives';
import { CasinoWageringFields, SportsbookWageringFields } from './welcome-bonus/WageringFields';
import {
  WAGERING_CONFIGURATION_DEFAULT_VALUES,
  buildWageringConfigurationPayload,
  getWageringConfigurationFormData,
} from './welcome-bonus/wageringConfigurationAdapter';

const styles = {
  loading: {
    minHeight: 240,
    display: 'grid',
    placeItems: 'center',
  },
  configurationGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
    gap: 2,
    alignItems: 'start',
  },
} as const;

const WageringConfigurationForm = ({ closeModal }: DialogType) => {
  const invalidateQuery = useInvalidateQuery();

  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();

  const configurationQuery = useWageringConfiguration();
  const form = useForm<input<typeof wageringConfigurationSchema>, unknown, WageringConfigurationFormData>({
    defaultValues: WAGERING_CONFIGURATION_DEFAULT_VALUES,
    resolver: zodResolver(wageringConfigurationSchema),
    mode: 'onChange',
  });
  const { reset } = form;

  const saveMutation = useMutation({
    mutationFn: (payload: WageringConfiguration) =>
      postData<WageringConfiguration>(API_ROUTES.WAGERING_CONFIGS, payload, 'wallet'),
    onSuccess: async () => {
      await invalidateQuery([QUERY_KEYS.wageringConfigs]);
      enqueueSnackbar({ message: 'Global wagering settings saved.', variant: 'success' });
      closeModal?.();
    },
    onError: handleErrorMessage,
  });

  useEffect(() => {
    if (configurationQuery.data === undefined || form.formState.isDirty) return;

    reset(getWageringConfigurationFormData(configurationQuery.data));
  }, [configurationQuery.data, form.formState.isDirty, reset]);

  const onSubmit = (data: WageringConfigurationFormData) => {
    saveMutation.mutate(buildWageringConfigurationPayload(data));
  };

  const disableSubmit =
    configurationQuery.isLoading || configurationQuery.isError || saveMutation.isPending || !form.formState.isDirty;

  return (
    <FormProvider {...form}>
      <FormModalLayout
        label='Global wagering settings'
        onSave={form.handleSubmit(onSubmit)}
        onClose={closeModal}
        actionButtonLabel='Save settings'
        showEditTabs={false}
        disableSubmit={disableSubmit}
        titleAlign='center'
      >
        <Alert severity='info'>
          These marketplace settings apply when wagering on the product opposite to a player&apos;s active Welcome
          Bonus. Wagering on the bonus product continues to use that campaign&apos;s settings. Until global settings are
          saved, Wallet counts opposite-product wagering 1:1.
        </Alert>

        {configurationQuery.isLoading && (
          <Box sx={styles.loading}>
            <CircularProgress aria-label='Loading global wagering settings' />
          </Box>
        )}

        {configurationQuery.isError && (
          <RetryAlert onRetry={configurationQuery.refetch}>Unable to load global wagering settings.</RetryAlert>
        )}

        {!configurationQuery.isLoading && !configurationQuery.isError && (
          <Box sx={styles.configurationGrid}>
            <WelcomeBonusFormSection title='Sportsbook settings'>
              <SportsbookWageringFields />
            </WelcomeBonusFormSection>
            <WelcomeBonusFormSection title='Casino settings'>
              <CasinoWageringFields />
            </WelcomeBonusFormSection>
          </Box>
        )}
      </FormModalLayout>
    </FormProvider>
  );
};

export default WageringConfigurationForm;
